<template>
  <div>
    <div class="flex justify-between items-center mb-1">
      <label class="block text-sm font-semibold">
        {{ $t("message.image_generator.style_assets") }}
      </label>
      <button
        class="text-xs text-red-600 hover:underline"
        @click="showForm = !showForm"
      >
        <i class="mdi mdi-plus"></i>
        {{ $t("message.image_generator.add_asset") }}
      </button>
    </div>

    <label class="flex items-center gap-1 text-xs text-gray-600 mb-2">
      <input
        type="checkbox"
        :checked="includeDefaults"
        @change="$emit('update:includeDefaults', $event.target.checked)"
      />
      {{ $t("message.image_generator.include_defaults") }}
    </label>

    <!-- Biblioteca -->
    <div v-if="assets.length" class="flex flex-wrap gap-2 mb-2">
      <div
        v-for="asset in assets"
        :key="asset.id"
        class="relative w-20 border-2 rounded overflow-hidden"
        :class="borderClass(asset)"
      >
        <button class="block w-full" @click="toggle(asset)">
          <img
            :src="asset.url"
            class="w-full h-16 object-contain bg-gray-100"
            :title="asset.description || ''"
          />
          <span class="block text-xs truncate px-1 py-0.5">
            {{ asset.name }}
          </span>
        </button>
        <span
          class="absolute top-0 left-0 text-xs px-1 rounded-br"
          :class="
            asset.isDefault
              ? 'bg-red-600 text-white'
              : 'bg-gray-700 text-white bg-opacity-70'
          "
        >
          {{
            asset.isDefault ? $t("message.image_generator.always") : asset.type
          }}
        </span>
        <button
          class="absolute top-0 right-0 bg-black bg-opacity-50 text-white px-1 leading-none"
          :title="$t('message.image_generator.delete_asset')"
          @click="remove(asset)"
        >
          <i class="mdi mdi-close text-xs"></i>
        </button>
      </div>
    </div>
    <p v-else-if="!loading" class="text-xs text-gray-400 mb-2">
      {{ $t("message.image_generator.no_assets") }}
    </p>

    <!-- Alta de asset -->
    <div v-if="showForm" class="border rounded p-3 mb-3 bg-gray-50">
      <div
        class="border-2 border-dashed rounded p-3 text-center text-xs cursor-pointer mb-2"
        :class="dragging ? 'border-red-600 bg-red-50' : 'border-gray-300'"
        @click="$refs.assetInput.click()"
        @dragover.prevent="dragging = true"
        @dragleave.prevent="dragging = false"
        @drop.prevent="onDrop"
      >
        <img
          v-if="form.data"
          :src="`data:${form.mimeType};base64,${form.data}`"
          class="h-16 mx-auto object-contain"
        />
        <template v-else>
          <i class="mdi mdi-image-plus text-xl block text-gray-400"></i>
          {{ $t("message.image_generator.drop_here") }}
        </template>
      </div>
      <input
        ref="assetInput"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        class="hidden"
        @change="onFile"
      />

      <input
        v-model="form.name"
        type="text"
        class="regular clear full mb-2"
        :placeholder="$t('message.image_generator.asset_name')"
      />
      <select v-model="form.type" class="regular clear full mb-2">
        <option value="logo">{{
          $t("message.image_generator.type_logo")
        }}</option>
        <option value="style">
          {{ $t("message.image_generator.type_style") }}
        </option>
        <option value="other">
          {{ $t("message.image_generator.type_other") }}
        </option>
      </select>
      <!-- Este texto viaja al modelo justo antes de la imagen. -->
      <textarea
        v-model="form.description"
        rows="2"
        class="regular clear full mb-1"
        :placeholder="$t('message.image_generator.asset_label_placeholder')"
      ></textarea>
      <p class="text-xs text-gray-500 mb-2">
        {{ $t("message.image_generator.asset_label_hint") }}
      </p>
      <label class="flex items-center gap-1 text-xs mb-2">
        <input type="checkbox" v-model="form.isDefault" />
        {{ $t("message.image_generator.asset_is_default") }}
      </label>
      <button
        class="w-full bg-gray-800 text-white text-sm py-1 rounded disabled:opacity-50"
        :disabled="saving || !form.name.trim() || !form.data"
        @click="save()"
      >
        {{
          saving
            ? $t("message.image_generator.saving")
            : $t("message.image_generator.save_asset")
        }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  getImageAssets,
  createImageAsset,
  deleteImageAsset
} from "@/api/store/AiService.js";

export default {
  name: "AiStyleAssets",
  props: {
    // Ids seleccionados (los que no son por defecto).
    value: {
      type: Array,
      default: () => []
    },
    includeDefaults: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      assets: [],
      loading: false,
      showForm: false,
      saving: false,
      dragging: false,
      form: this.emptyForm()
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    // CI devuelve los enteros/booleanos de MySQL como strings: "0" es truthy
    // en JS, así que hay que convertirlos antes de usarlos en condiciones.
    normalize(asset) {
      return {
        ...asset,
        id: Number(asset.id),
        isDefault: !!Number(asset.isDefault)
      };
    },
    emptyForm() {
      return {
        name: "",
        type: "style",
        description: "",
        isDefault: false,
        mimeType: null,
        data: null
      };
    },
    async load() {
      this.loading = true;
      try {
        const data = await getImageAssets();
        this.assets = (data.assets || []).map(this.normalize);
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.assets_error"));
      } finally {
        this.loading = false;
      }
    },
    borderClass(asset) {
      if (asset.isDefault) {
        return this.includeDefaults
          ? "border-red-600"
          : "border-gray-200 opacity-50";
      }
      return this.value.includes(asset.id)
        ? "border-red-600"
        : "border-gray-200";
    },
    toggle(asset) {
      // Los marcados por defecto los adjunta el backend: acá no se seleccionan.
      if (asset.isDefault) return;
      const id = asset.id;
      const selected = this.value.includes(id)
        ? this.value.filter(current => current !== id)
        : [...this.value, id];
      this.$emit("input", selected);
    },
    onDrop(event) {
      this.dragging = false;
      const file = Array.from(event.dataTransfer.files || []).find(f =>
        f.type.startsWith("image/")
      );
      if (file) this.readFile(file);
    },
    onFile(event) {
      const file = (event.target.files || [])[0];
      if (file) this.readFile(file);
      event.target.value = "";
    },
    readFile(file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.form.mimeType = file.type;
        this.form.data = String(reader.result).split(",")[1];
        if (!this.form.name) {
          this.form.name = file.name.replace(/\.[^.]+$/, "");
        }
      };
      reader.readAsDataURL(file);
    },
    async save() {
      this.saving = true;
      try {
        const data = await createImageAsset({
          name: this.form.name.trim(),
          type: this.form.type,
          description: this.form.description.trim(),
          isDefault: this.form.isDefault,
          image: { mimeType: this.form.mimeType, data: this.form.data }
        });
        this.assets.push(this.normalize(data.asset));
        this.form = this.emptyForm();
        this.showForm = false;
        this.$toast.success(this.$t("message.image_generator.asset_saved"));
      } catch (e) {
        this.$toast.error(
          e?.response?.data?.error ||
            this.$t("message.image_generator.assets_error")
        );
      } finally {
        this.saving = false;
      }
    },
    async remove(asset) {
      if (!confirm(this.$t("message.image_generator.confirm_delete_asset"))) {
        return;
      }
      try {
        await deleteImageAsset(asset.id);
        this.assets = this.assets.filter(a => a.id !== asset.id);
        this.$emit(
          "input",
          this.value.filter(id => id !== asset.id)
        );
      } catch (e) {
        this.$toast.error(this.$t("message.image_generator.assets_error"));
      }
    }
  }
};
</script>
